import { MouseEvent } from 'react';
import { useTranslation } from 'src/hooks/use-translation';
import Box from '@mui/material/Box';

// ----------------------------------------------------------------------

/** Id of the page's `<main>` landmark — the skip link's target. */
export const MAIN_CONTENT_ID = 'main-content';

/**
 * The first tab stop on the page: lets keyboard users jump past the header and sidebar
 * straight to the content. Hidden until it receives focus.
 */
export function SkipLink() {
  const { t } = useTranslation('', { keyPrefix: 'header' });

  // Move focus programmatically instead of following the hash, so the URL (and history) stay clean.
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(MAIN_CONTENT_ID)?.focus();
  };

  return (
    <Box
      component="a"
      href={`#${MAIN_CONTENT_ID}`}
      onClick={handleClick}
      sx={{
        position: 'fixed',
        top: 12,
        left: 12,
        zIndex: (theme) => theme.zIndex.tooltip + 1,
        px: 2,
        py: 1.25,
        borderRadius: 1,
        typography: 'subtitle2',
        color: 'text.primary',
        textDecoration: 'none',
        bgcolor: 'background.paper',
        boxShadow: (theme) => theme.customShadows.dropdown,
        transform: 'translateY(-200%)',
        '&:focus': { transform: 'none' },
      }}
    >
      {t('skip-to-content')}
    </Box>
  );
}
